<template>
    <CardSelect :app="app" :card="card" :actionable="false">

        <template #title>
            Select a flow to create your 3D model.
        </template>

        <template #options>

            <OptionIcon 
                v-for="option in options" 
                :id="option.id" 
                :index="option.index"
                :icon="option.icon" 
                :label="option.label" 
                :description="option.description" 
                :selected="selected === option.id" 
                @click="handleOption(option)" 
                @mouseenter="handleHover(option)"
                @mouseleave="handleHover(null)">
            </OptionIcon>

        </template>

        <template #post>
            <AtomText  class="hide-on-mobile" :text="guidance" />
        </template>

    </CardSelect>
</template>

<script>
import CATEGORY from '@generio-ai/library-legacy/constants/category'
import { mapGetters, mapActions } from '@generio-ai/library-legacy/plugins/vuex'
import CARD from '@/constants/card.js'
import OPTION from '@/constants/option.js'
import AtomText from '@/components/common/atom/AtomText.vue'
import CardSelect from '@/components/flow/cards/CardSelect.vue'
import OptionIcon from '@/components/flow/options/OptionIcon.vue'

export default {
    name: 'CardSelectFlow',
    components: {
        CardSelect,
        OptionIcon,
        AtomText
    },
    props: {
        app: {
            type: String,
            required: true
        },
        card: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            hovered: null
        }
    },
    computed: {
        ...mapGetters('flows', [
            'isActiveByIndex',
            'isLastByIndex'
        ]),

        isActiveCard() {
			return this.isActiveByIndex(this.app, this.index)
		},
        isLastCard() {
            return this.isLastByIndex(this.app, this.index)
        },

        index() {
            return this.card.index
        },
        selected() {
            return this.card.option
        },

        options() {
            return [
                {
                    index: 1,
                    ...OPTION.SELECT_FLOW_PROMPT,
                },
                {
                    index: 2,
                    ...OPTION.SELECT_FLOW_IMAGES,
                },
                {
                    index: 3,
                    ...OPTION.SELECT_FLOW_SKETCH,
                },
            ]
        },
        guidance() {
            switch (this.hovered) {
                case OPTION.SELECT_FLOW_PROMPT.id:
                    return OPTION.SELECT_FLOW_PROMPT.guidance
                case OPTION.SELECT_FLOW_IMAGES.id:
                    return OPTION.SELECT_FLOW_IMAGES.guidance
                case OPTION.SELECT_FLOW_SKETCH.id: 
                    return OPTION.SELECT_FLOW_SKETCH.guidance
            }
            return ''
        }
    },
    methods: {
        ...mapActions('flows', [
            'newTransaction',
            'commitCreateAssets',
            'fetchBase',
        ]),
        handleHover(option) {
            this.hovered = option?.id
        },
        handleOption(option) 
        {
            // update card
            this.card.option = option.id

            switch(option.id) 
            {
                case OPTION.SELECT_FLOW_PROMPT.id:
                    this.newTransaction({
                        app: this.app,
                        card: this.card,
                        taskCommit: this.commitCreateAssets(),
                        taskFetch: this.fetchBase({
                            component: CARD.PROMPT,
                            category: CATEGORY.PRIMITIVE
                        })
                    })
                    break

                case OPTION.SELECT_FLOW_IMAGES.id:
                    this.newTransaction({
                        app: this.app,
                        card: this.card,
                        taskCommit: this.commitCreateAssets(),
                        taskFetch: this.fetchBase({
                            component: CARD.IMPORT,
                            category: CATEGORY.PHOTO
                        })
                    })
                    break

                case OPTION.SELECT_FLOW_SKETCH.id:
                    this.newTransaction({
                        app: this.app,
                        card: this.card,
                        taskCommit: this.commitCreateAssets(),
                        taskFetch: this.fetchBase({
                            component: CARD.IMAGE,
                            category: CATEGORY.SKETCH
                        })
                    })
                    break
            }
        },
        handleKeyboardShortcuts(event) 
        {
            // only if the card is active
            if (!this.isActiveCard) return

            // Get the pressed key
            const key = event.key

            // Check if the key is a single digit (1-9)
            if (/^[1-9]$/.test(key)) {

                // Convert to 0-indexed position
                const index = parseInt(key, 10) - 1

                // If an option exists at that index, trigger it
                if (index >= 0 && index < this.options.length) {

                    event.preventDefault()
                    this.handleOption(this.options[index])
                }
            }
        }
    },
    mounted() {
		window.addEventListener('keydown', this.handleKeyboardShortcuts)
	},
	beforeUnmount() {
        window.removeEventListener('keydown', this.handleKeyboardShortcuts)
    }
}
</script>